import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { 
  RefreshCcw, 
  Network, 
  FileSearch, 
  ShieldCheck,
  FileCheck,
  Truck,
  Target,
  Eye,
  Sparkles,
  LucideIcon
} from 'lucide-react';
import { useJsonData } from '../../hooks/useJsonData';
import { useSafeAnimation, useSafeVariants } from '../ui/SafeMotion';

// Icon mapping
const iconMap: Record<string, LucideIcon> = {
  Network, ShieldCheck, FileSearch, FileCheck, Truck, RefreshCcw
};

interface Feature {
  icon: string;
  title: string;
  description: string;
}

interface WhyEcoEnviroData {
  features: Feature[];
}

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
};

export function WhyEcoEnviro() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { data, loading } = useJsonData<WhyEcoEnviroData>('/data/why-eco-enviro.json');

  // SSR-safe animation props
  const leftAnim = useSafeAnimation(
    { opacity: 0, x: -30 },
    { opacity: 1, x: 0 },
    isInView
  );
  const rightAnim = useSafeAnimation(
    { opacity: 0, x: 30 },
    { opacity: 1, x: 0 },
    isInView
  );
  const badgeAnim = useSafeAnimation(
    { opacity: 0, scale: 0.9 },
    { opacity: 1, scale: 1 },
    isInView
  );
  const cardAnim = useSafeAnimation(
    { opacity: 0, y: 20 },
    { opacity: 1, y: 0 },
    isInView
  );
  const variants = useSafeVariants(isInView);

  const features = data?.features || [];

  return (
    <section ref={ref} className="py-20 lg:py-28 bg-gradient-to-b from-white to-eco-green-50 overflow-hidden relative">
      <div className="absolute top-1/2 left-0 w-96 h-96 bg-eco-green-100 rounded-full blur-3xl opacity-50 -translate-x-1/2 -translate-y-1/2" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={leftAnim.initial}
            animate={leftAnim.animate}
            transition={{ duration: 0.6 }}
          >
            <motion.span
              initial={badgeAnim.initial}
              animate={badgeAnim.animate}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-eco-green-100 border border-eco-green-200 text-eco-green-700 text-sm font-semibold rounded-full mb-6"
            >
              <Sparkles className="w-4 h-4" />
              Why Choose Eco Enviro
            </motion.span>
            <h2 className="text-4xl sm:text-5xl font-bold text-eco-green-900 mb-6 leading-tight">
              Your Trusted{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-eco-green-600 to-eco-green-800">
                Waste Partner
              </span>
            </h2>
            <p className="text-gray-600 text-lg mb-8 leading-relaxed">
              Founded in 2020, Eco Enviro Circular Solutions provides end-to-end waste management, 
              recycling, and RDF solutions for corporates and industries across India.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              <motion.div
                initial={cardAnim.initial}
                animate={cardAnim.animate}
                transition={{ delay: 0.3 }}
                whileHover={{ y: -5 }}
                className="bg-white/80 backdrop-blur-md rounded-2xl p-5 shadow-xl shadow-eco-green-100/50 border border-eco-green-100/80"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-eco-green-500 to-eco-green-700 rounded-xl flex items-center justify-center mb-4">
                  <Eye className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-bold text-eco-green-900 text-lg mb-2">Vision</h4>
                <p className="text-gray-600 text-sm">Zero waste to landfill through circular systems.</p>
              </motion.div>
              <motion.div
                initial={cardAnim.initial}
                animate={cardAnim.animate}
                transition={{ delay: 0.4 }}
                whileHover={{ y: -5 }}
                className="bg-white/80 backdrop-blur-md rounded-2xl p-5 shadow-xl shadow-eco-green-100/50 border border-eco-green-100/80"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-eco-green-600 to-eco-green-800 rounded-xl flex items-center justify-center mb-4">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-bold text-eco-green-900 text-lg mb-2">Mission</h4>
                <p className="text-gray-600 text-sm">Recover maximum value. Ensure compliant disposal.</p>
              </motion.div>
            </div>
          </motion.div>

          <motion.div
            initial={rightAnim.initial}
            animate={rightAnim.animate}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Loading State */}
            {loading && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-32 bg-gray-100 rounded-2xl animate-pulse" />
                ))}
              </div>
            )}

            {/* Features Grid */}
            {!loading && (
              <motion.div
                variants={containerVariants}
                initial={variants.initial}
                animate={variants.animate}
                className="grid grid-cols-2 sm:grid-cols-3 gap-4"
              >
                {features.map((feature) => {
                  const IconComponent = iconMap[feature.icon] || Network;
                  return (
                    <motion.div
                      key={feature.title}
                      variants={itemVariants}
                      whileHover={{ y: -8, scale: 1.03 }}
                      className="bg-white/80 backdrop-blur-md rounded-2xl p-5 text-center shadow-lg shadow-eco-green-100/40 border border-eco-green-100/60 hover:border-eco-green-300 hover:shadow-xl hover:bg-white/95 transition-all cursor-default"
                    >
                      <motion.div 
                        className="w-12 h-12 mx-auto mb-3 bg-gradient-to-br from-eco-green-500 to-eco-green-700 rounded-xl flex items-center justify-center shadow-md"
                        whileHover={{ rotate: 10 }}
                      >
                        <IconComponent className="w-6 h-6 text-white" />
                      </motion.div>
                      <p className="text-sm font-bold text-eco-green-900 mb-1">{feature.title}</p>
                      <p className="text-xs text-gray-500">{feature.description}</p>
                    </motion.div>
                  );
                })}
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
